import React, { useEffect, useId, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { authService } from '../../services/api';
import { getApiUserCode } from './ApiUserBadge';
import './ResourceSidebar.css';

const SIDEBAR_COLLAPSED_KEY = 'tikwiz-resource-sidebar-collapsed';

const readCollapsedPreference = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const accountLinks = [
  { label: 'Dice', path: '/user/accounts/dice' },
  { label: 'Belly Up Aspen', path: '/user/accounts/belly-up' },
  { label: 'Pacha', path: '/user/accounts/pacha' },
  { label: 'TAO', path: '/user/accounts/tao' },
  { label: 'Get-In', path: '/user/accounts/get-in' },
  { label: 'Seetickets', path: '/user/accounts/seetickets' },
  { label: 'Ticketmaster', path: '/user/accounts/ticketmaster' },
  { label: 'AXS', path: '/user/accounts/axs' },
  { label: 'Tixr', path: '/user/accounts/tixr' },
];

const otherAccountOptions = [
  'Shotgun',
  'Fever',
  'More',
  'Ticketservices',
  'Cavo',
  'Showclix',
  'Frontgate',
  'Other',
];

const infoLinks = [
  { label: 'Artists', path: '/user/info/artists', public: true },
  { label: 'Venues', path: '/user/info/venues', public: true },
  { label: 'Ticket Types', path: '/user/info/ticket-types', public: true },
  { label: 'Emails', path: '/user/info/emails' },
  { label: 'Cards', path: '/user/info/cards' },
  { label: 'Phone Numbers', path: '/user/info/phones' },
  { label: 'Contacts', path: '/user/info/contacts', public: true },
];

const transactionLinks = [
  { label: 'Orders', path: '/user/transactions/orders' },
  { label: 'Purchases', path: '/user/transactions/purchases' },
  { label: 'Payments', path: '/user/transactions/payments', restrictedCodes: ['TS', 'DV'] },
];

const SidebarLink = ({ link }) => (
  <NavLink to={link.path} className={({ isActive }) => `resource-sidebar-link resource-sidebar-child ${isActive ? 'active' : ''}`}>
    {link.label}
  </NavLink>
);

const ResourceSidebar = ({ section = 'info' }) => {
  const [collapsed, setCollapsed] = useState(readCollapsedPreference);
  const navigationId = useId();
  const [othersOpen, setOthersOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      // Collapsing still works when browser storage is unavailable.
    }
  }, [collapsed]);
  const [apiUserCode, setApiUserCode] = useState('');
  const showAccounts = section === 'accounts';
  const showInfo = section === 'info';
  const showVenuesOnly = section === 'venues';
  const showTransactions = section === 'transactions';
  const canSeePrivateInfo = ['TS', 'DV'].includes(apiUserCode);
  const visibleInfoLinks = useMemo(() => (
    infoLinks.filter((link) => link.public || canSeePrivateInfo)
  ), [canSeePrivateInfo]);
  const visibleTransactionLinks = useMemo(() => (
    transactionLinks.filter((link) => !link.restrictedCodes || link.restrictedCodes.includes(apiUserCode))
  ), [apiUserCode]);

  useEffect(() => {
    let isMounted = true;
    const token = authService.getToken();

    if (!token) {
      setApiUserCode('');
      return () => {
        isMounted = false;
      };
    }

    authService.getCurrentUser(token)
      .then((user) => {
        if (isMounted) {
          setApiUserCode(getApiUserCode(user.email));
        }
      })
      .catch(() => {
        if (isMounted) {
          setApiUserCode('');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <aside className={`resource-sidebar${collapsed ? ' resource-sidebar-collapsed' : ''}`}>
      <div className="resource-sidebar-controls">
        <button
          type="button"
          className="resource-sidebar-collapse-button"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          aria-controls={navigationId}
          onClick={() => setCollapsed((value) => !value)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d={collapsed ? 'm9 5 7 7-7 7' : 'm15 5-7 7 7 7'} />
          </svg>
        </button>
      </div>
      <nav id={navigationId} className="resource-sidebar-nav" aria-label="Resources" hidden={collapsed}>
        {showAccounts && (
          <section className="resource-sidebar-section">
            <div className="resource-sidebar-heading">
              Accounts
            </div>
            <div className="resource-sidebar-children">
              {accountLinks.map((link) => (
                <SidebarLink key={link.path} link={link} />
              ))}
              <button
                className={`resource-sidebar-link resource-sidebar-child resource-sidebar-toggle ${othersOpen ? 'active-soft' : ''}`}
                type="button"
                onClick={() => setOthersOpen((current) => !current)}
              >
                Other Vendors
              </button>
              {othersOpen && (
                <div className="resource-sidebar-others">
                  {otherAccountOptions.map((option) => (
                    <span key={option} className="resource-sidebar-disabled">
                      {option}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {showInfo && (
          <section className="resource-sidebar-section">
            <div className="resource-sidebar-heading">
              Info
            </div>
            <div className="resource-sidebar-children">
              {visibleInfoLinks.map((link) => (
                <SidebarLink key={link.path} link={link} />
              ))}
            </div>
          </section>
        )}

        {showTransactions && (
          <section className="resource-sidebar-section">
            <div className="resource-sidebar-heading">
              Transactions
            </div>
            <div className="resource-sidebar-children">
              {visibleTransactionLinks.map((link) => (
                <SidebarLink key={link.path} link={link} />
              ))}
            </div>
          </section>
        )}

        {showVenuesOnly && (
          <section className="resource-sidebar-section">
            <div className="resource-sidebar-children">
              <NavLink to="/user/info/venues" className={({ isActive }) => `resource-sidebar-link resource-sidebar-child ${isActive ? 'active' : ''}`}>
                Venues
              </NavLink>
            </div>
          </section>
        )}
      </nav>
    </aside>
  );
};

export default ResourceSidebar;
